import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";

const LIST_URL = `${API_BASE_URL}/api/admin/qa/question_audits/get_past_paper_filenames`;
const VERIFY_URL = `${API_BASE_URL}/api/admin/qa/question_audits/verify`;

const FIELD_LABELS = {
  content: "Question text",
  options: "Options",
  answer: "Answer",
  explanation: "Explanation",
  case_sensitive: "Case sensitive",
  question_type: "Question type",
  exam_year: "Exam year",
};

function downloadJson(filename, obj) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function toText(val) {
  if (val === null || val === undefined) return "";
  if (Array.isArray(val)) return val.join("\n");
  return String(val);
}

export default function QuestionAudit() {
  const [files, setFiles] = useState([]);
  const [directory, setDirectory] = useState("");
  const [selectedFile, setSelectedFile] = useState("");

  const [loadingList, setLoadingList] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [error, setError] = useState("");

  const [report, setReport] = useState(null);

  // UI state
  const [tab, setTab] = useState("mismatches"); // mismatches | missing | extra
  const [search, setSearch] = useState("");
  const [fieldFilter, setFieldFilter] = useState("all");
  const [expandedQnum, setExpandedQnum] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoadingList(true);
      setError("");

      try {
        const { data } = await axios.get(LIST_URL);
        if (cancelled) return;

        setFiles(data.files || []);
        setDirectory(data.directory || "");

        const first = (data.files || [])[0] || "";
        setSelectedFile(first);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.error || e?.message || "Failed to load documents.");
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  async function runVerify() {
    if (!selectedFile) return;

    setLoadingVerify(true);
    setError("");
    setReport(null);
    setExpandedQnum(null);

    try {
      const { data } = await axios.post(VERIFY_URL, { filename: selectedFile });
      setReport(data);
      setTab("mismatches");
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Verification failed.");
    } finally {
      setLoadingVerify(false);
    }
  }

  const mismatchGroups = useMemo(() => {
    if (!report?.mismatches) return [];

    const s = search.trim().toLowerCase();
    const ff = fieldFilter;

    return report.mismatches
      .map((m) => {
        const diffs = (m.diffs || []).filter((d) => {
          if (ff !== "all" && d.field !== ff) return false;
          if (!s) return true;
          return (
            String(m.question_number).toLowerCase().includes(s) ||
            String(d.field).toLowerCase().includes(s) ||
            toText(d.doc).toLowerCase().includes(s) ||
            toText(d.db).toLowerCase().includes(s)
          );
        });
        return { ...m, diffs };
      })
      .filter((m) => (m.diffs || []).length > 0);
  }, [report, search, fieldFilter]);

  const counts = useMemo(() => {
    return {
      missing: report?.missing_in_db?.length || 0,
      extra: report?.extra_in_db?.length || 0,
      mismatches: report?.mismatches?.length || 0,
    };
  }, [report]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold">Question Audit</h1>
            <p className="mt-1 text-sm text-gray-600">Compare a DOCX (source-of-truth) against DB questions.</p>
            {directory && <p className="mt-1 text-xs text-gray-500">DOCX directory: {directory}</p>}
          </div>

          {report && (
            <button
              className="rounded-xl border bg-white px-4 py-2 text-sm hover:bg-gray-50"
              onClick={() =>
                downloadJson(
                  `audit_${report.doc_external_id || "unknown"}_${new Date()
                    .toISOString()
                    .slice(0, 19)
                    .replaceAll(":", "-")}.json`,
                  report
                )
              }
            >
              Download JSON
            </button>
          )}
        </div>

        <div className="mt-6 rounded-2xl border bg-white p-4 shadow-sm">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="text-sm font-medium">Select Documented Past Paper</label>
              <div className="mt-2 flex gap-2">
                <select
                  className="w-full rounded-xl border px-3 py-2 text-sm"
                  value={selectedFile}
                  onChange={(e) => setSelectedFile(e.target.value)}
                  disabled={loadingList}
                >
                  {files.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
                <button
                  className="rounded-xl border px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => window.location.reload()}
                  disabled={loadingList}
                  title="Reload list"
                >
                  ↻
                </button>
              </div>

              {loadingList && <div className="mt-2 text-xs text-gray-500">Loading documents…</div>}
              {!loadingList && files.length === 0 && (
                <div className="mt-2 text-xs text-red-600">No .docx files found in the documented_past_papers folder.</div>
              )}
            </div>
          </div>

          {error && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
          )}

          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              className="rounded-xl bg-black px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              onClick={runVerify}
              disabled={loadingVerify || !selectedFile}
            >
              {loadingVerify ? "Running…" : "Run Verification"}
            </button>
          </div>
        </div>

        {report && (
          <>
            <div className="mt-6 grid gap-3 md:grid-cols-3">
              <StatCard label="Computed Document External ID" value={report.computed_doc_external_id} />
              <StatCard label="Parsed Questions From Document" value={report.parsed_count} />
              <StatCard label="Loaded Questions from DB" value={report.db_count} />
            </div>

            <div className="mt-4 rounded-2xl border bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <TabButton active={tab === "mismatches"} onClick={() => setTab("mismatches")}>
                  Mismatches ({counts.mismatches})
                </TabButton>
                <TabButton active={tab === "missing"} onClick={() => setTab("missing")}>
                  Missing in DB ({counts.missing})
                </TabButton>
                <TabButton active={tab === "extra"} onClick={() => setTab("extra")}>
                  Extra in DB ({counts.extra})
                </TabButton>

                {tab === "mismatches" && (
                  <div className="ml-auto flex flex-wrap items-center gap-2">
                    <select
                      className="rounded-xl border px-3 py-2 text-sm"
                      value={fieldFilter}
                      onChange={(e) => setFieldFilter(e.target.value)}
                    >
                      <option value="all">All fields</option>
                      {Object.keys(FIELD_LABELS).map((k) => (
                        <option key={k} value={k}>
                          {FIELD_LABELS[k]}
                        </option>
                      ))}
                    </select>

                    <input
                      className="rounded-xl border px-3 py-2 text-sm"
                      placeholder="Search q#, field, text…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                )}
              </div>

              {tab === "missing" && <ListPanel title="Missing questions (in DOCX, not in DB)" items={report.missing_in_db} />}
              {tab === "extra" && <ListPanel title="Extra questions (in DB, not in DOCX)" items={report.extra_in_db} />}

              {tab === "mismatches" && (
                <div className="mt-4 space-y-3">
                  {mismatchGroups.length === 0 ? (
                    <div className="rounded-xl border p-4 text-sm">No mismatches 🎉</div>
                  ) : (
                    mismatchGroups.map((m) => (
                      <div key={m.question_number} className="rounded-xl border">
                        <button
                          className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50"
                          onClick={() => setExpandedQnum(expandedQnum === m.question_number ? null : m.question_number)}
                        >
                          {/* LEFT */}
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="font-medium truncate">
                              Question #{m.question_number}
                              <span className="ml-2 text-xs text-gray-500">
                                ({m.diffs.length} diff{m.diffs.length === 1 ? "" : "s"})
                              </span>
                            </div>
                          </div>

                          {/* RIGHT */}
                          <div className="flex items-center gap-3 shrink-0">
                            {m.db_question_id && (
                              <span className="text-xs font-mono text-gray-500">ID: {m.db_question_id}</span>
                            )}
                            <span className="text-sm">{expandedQnum === m.question_number ? "▾" : "▸"}</span>
                          </div>
                        </button>

                        {expandedQnum === m.question_number && (
                          <div className="px-4 pb-4 space-y-4">
                            {m.diffs.map((d, idx) => (
                              <div key={idx} className="pt-3 border-t">
                                <div className="text-sm font-semibold">{FIELD_LABELS[d.field] || d.field}</div>
                                <div className="mt-2 grid gap-2 md:grid-cols-2">
                                  <ValueBox label="DOC" value={d.doc} />
                                  <ValueBox label="DB" value={d.db} />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-1 text-sm font-semibold break-words">{value ?? "—"}</div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      className={["rounded-xl border px-3 py-2 text-sm", active ? "bg-black text-white" : "bg-white hover:bg-gray-50"].join(
        " "
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function ListPanel({ title, items }) {
  return (
    <div className="mt-4 rounded-xl border p-4">
      <div className="text-sm font-semibold">{title}</div>
      <div className="mt-2 text-sm text-gray-700">{items?.length ? items.join(", ") : "None 🎉"}</div>
    </div>
  );
}

function ValueBox({ label, value }) {
  return (
    <div className="rounded-xl border p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <pre className="mt-2 whitespace-pre-wrap text-xs leading-relaxed">{toText(value)}</pre>
    </div>
  );
}
